import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Button, Select, Space, message, Spin, Card } from 'antd';
import { useDispatch } from 'react-redux';
import { setBreadcrumbList } from '../../../store/menuInfo/menuInfo';
import apis from '../../../apis';
import './index.scss';

const { Option } = Select;
const { TextArea } = Input;

const PlanEdit = ({ id, type, onBack }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [detail, setDetail] = useState({});
    const [allVersions, setAllVersions] = useState([]);
    const [currentVersionId, setCurrentVersionId] = useState(null);
    const [initialValues, setInitialValues] = useState({});
    const [isFormChanged, setIsFormChanged] = useState(false);
    const [productList, setProductList] = useState([]);
    const dispatch = useDispatch();

    const isEdit = !!id;
    const isView = type === 'view';

    useEffect(() => {
        let title = '新增方案';
        if (isView) title = '查看方案';
        else if (isEdit) title = '编辑方案';

        dispatch(setBreadcrumbList([
            { title: '充值方案' },
            { title: 'TOC方案管理', onClick: onBack },
            { title }
        ]));
    }, [dispatch, isEdit, isView, onBack]);

    useEffect(() => {
        apis.getProductList('TOC').then(res => {
            if (res.code === 200) {
                const items = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.data || []);
                setProductList(items);
            }
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (isEdit || isView) {
            setLoading(true);
            apis.getTocTopupPlanDetail({ topup_plan_id: id }).then(res => {
                if (res.code === 200) {
                    const data = res.data || {};
                    const versions = Array.isArray(data.versions) ? data.versions : [];
                    setAllVersions(versions);
                    const plan = data.plan || {};
                    const selected = data.selected_version || {};
                    const merged = { ...plan, ...selected };
                    setDetail(merged);
                    setInitialValues(merged);
                    setIsFormChanged(false);
                    setCurrentVersionId(selected.topup_plan_ver_id || null);
                    form.setFieldsValue(merged);
                } else {
                    message.error(res.message || '获取详情失败');
                }
            }).catch(() => {
                message.error('获取详情失败');
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [id, form, isEdit, isView]);

    const handleValuesChange = (changedValues, allValues) => {
        if (!isEdit) return;
        const fieldsToCheck = ['topup_plan_name', 'credits', 'original_price', 'discount_price', 'topup_plan_desc'];
        const hasChange = fieldsToCheck.some(key => {
            const initVal = initialValues[key];
            const currVal = allValues[key];
            if (key === 'topup_plan_name' || key === 'topup_plan_desc') {
                return (initVal || '') !== (currVal || '');
            }
            if (initVal === undefined || initVal === null) {
                return currVal !== undefined && currVal !== null && currVal !== '';
            }
            return Number(initVal) !== Number(currVal);
        });
        setIsFormChanged(hasChange);
    };

    const handleVersionChange = (verId) => {
        const selectedVer = allVersions.find(v => v.topup_plan_ver_id === verId);
        if (selectedVer) {
            const merged = { ...detail, ...selectedVer };
            setDetail(merged);
            setCurrentVersionId(verId);
            form.setFieldsValue(merged);
            setInitialValues(merged);
            setIsFormChanged(false);
        }
    };

    const handleSave = () => {
        form.validateFields().then(values => {
            setSubmitting(true);

            if (isEdit) {
                const updatePayload = {
                    topup_plan_id: id,
                    topup_plan_name: values.topup_plan_name,
                    topup_plan_desc: values.topup_plan_desc,
                    credits: values.credits,
                    original_price: values.original_price,
                    discount_price: values.discount_price,
                };
                apis.updateTocTopupPlan(updatePayload).then(res => {
                    if (res.code === 200) {
                        message.success('保存成功');
                        onBack();
                    } else {
                        message.error(res.message || '保存失败');
                    }
                }).catch(() => {
                    message.error('保存失败');
                }).finally(() => {
                    setSubmitting(false);
                });
            } else {
                const addPayload = {
                    topup_plan_code: values.topup_plan_code,
                    topup_plan_type_code: values.topup_plan_type_code,
                    prd_id: values.prd_id,
                    topup_plan_name: values.topup_plan_name,
                    topup_plan_desc: values.topup_plan_desc,
                    credits: values.credits,
                    original_price: values.original_price,
                    discount_price: values.discount_price,
                };
                apis.addTocTopupPlan(addPayload).then(res => {
                    if (res.code === 200) {
                        message.success('保存成功');
                        onBack();
                    } else {
                        message.error(res.message || '保存失败');
                    }
                }).catch(() => {
                    message.error('保存失败');
                }).finally(() => {
                    setSubmitting(false);
                });
            }
        });
    };

    const getPlanTypeText = (code) => {
        if (code === 'PERSONAL' || code === 'PERSONAL_EDITION') return '个人版';
        if (code === 'SEAT' || code === 'ENTERPRISE_EDITION') return '企业版';
        return code || '-';
    };

    const getProductName = (prdId) => {
        if (!prdId) return '-';
        const found = productList.find(p => p.prd_id === prdId);
        return found?.prd_name || prdId;
    };

    return (
        <div className={`toc-plan-edit-container ${isView ? 'view-mode' : ''}`}>
            <Spin spinning={loading}>
                <div className="content-body">
                    <Card bordered={false}>
                        {isView && allVersions.length > 0 && (
                            <div style={{ position: 'absolute', right: 24, top: 24, zIndex: 1 }}>
                                <Select
                                    value={currentVersionId}
                                    onChange={handleVersionChange}
                                    style={{ width: 160 }}
                                    placeholder="选择版本"
                                >
                                    {allVersions.map(ver => (
                                        <Option key={ver.topup_plan_ver_id} value={ver.topup_plan_ver_id}>
                                            {ver.ver_no} ({ver.ver_status_code === 'PUBLISHED' ? '已发布' : (ver.ver_status_code === 'REJECTED' ? '已驳回' : (ver.ver_status_code === 'AUDITING' ? '审核中' : '待发布'))})
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        )}
                        <div className="itemTitle2">
                            <span>
                                01<i></i>
                            </span>
                            基本信息
                        </div>

                        <Form
                            form={form}
                            onValuesChange={handleValuesChange}
                            layout="horizontal"
                            initialValues={{
                                topup_plan_type_code: 'PERSONAL',
                            }}
                            labelCol={{ flex: '90px' }}
                            wrapperCol={{ flex: 'auto' }}
                            labelAlign="left"
                            colon={false}
                        >
                            <div className="subscriptionItemsContent">
                                <Form.Item
                                    name="prd_id"
                                    label="产品"
                                    rules={[{ required: true, message: '请选择产品' }]}
                                >
                                    {isView ? (
                                        <span className="view-text">{getProductName(detail.prd_id)}</span>
                                    ) : (
                                        <Select
                                            placeholder="请选择产品"
                                            disabled={isEdit}
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {productList.map(p => (
                                                <Option key={p.prd_id} value={p.prd_id}>
                                                    {p.prd_name || p.prd_id}
                                                </Option>
                                            ))}
                                        </Select>
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="topup_plan_type_code"
                                    label="方案类别"
                                    rules={[{ required: true, message: '请选择方案类别' }]}
                                >
                                    {isView ? (
                                        <span className="view-text">{getPlanTypeText(detail.topup_plan_type_code)}</span>
                                    ) : (
                                        <Select placeholder="请选择方案类别" disabled={isEdit}>
                                            <Option value="SEAT">企业版</Option>
                                            <Option value="PERSONAL">个人版</Option>
                                        </Select>
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="topup_plan_code"
                                    label="方案编码"
                                    rules={[{ required: true, message: '请输入方案编码' }]}
                                >
                                    {isView ? (
                                        <span className="view-text">{detail.topup_plan_code || '-'}</span>
                                    ) : (
                                        <Input placeholder="请输入方案编码" disabled={isEdit} />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="topup_plan_name"
                                    label="方案名称"
                                    rules={[{ required: true, message: '请输入方案名称' }]}
                                >
                                    {isView ? (
                                        <span className="view-text">{detail.topup_plan_name || '-'}</span>
                                    ) : (
                                        <Input placeholder="请输入方案名称" />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="credits"
                                    label="积分"
                                    rules={[{ required: true, message: '请输入积分' }]}
                                >
                                    {isView ? (
                                        <span className="view-text">{detail.credits ?? '-'}</span>
                                    ) : (
                                        <InputNumber style={{ width: '100%' }} placeholder="请输入积分数" precision={0} min={0} />
                                    )}
                                </Form.Item>

                                <Form.Item
                                >
                                    <></>
                                </Form.Item>

                                <Form.Item
                                    name="original_price"
                                    label="原价 (元)"
                                    rules={[{ required: true, message: '请输入原价' }]}
                                >
                                    {isView ? (
                                        <span className="view-text">{detail.original_price !== undefined && detail.original_price !== null ? `¥${Number(detail.original_price).toFixed(2)}` : '-'}</span>
                                    ) : (
                                        <InputNumber style={{ width: '100%' }} placeholder="请输入原价" precision={2} min={0} />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="discount_price"
                                    label="折扣价 (元)"
                                    dependencies={['original_price']}
                                    rules={[
                                        { required: true, message: '请输入折扣价' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                const originalPrice = getFieldValue('original_price');
                                                if (value === undefined || value === null || originalPrice === undefined || originalPrice === null) {
                                                    return Promise.resolve();
                                                }
                                                if (Number(value) > Number(originalPrice)) {
                                                    return Promise.reject(new Error('折扣价不能大于原价'));
                                                }
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    {isView ? (
                                        <span className="view-text">{detail.discount_price !== undefined && detail.discount_price !== null && detail.discount_price !== '' ? `¥${Number(detail.discount_price).toFixed(2)}` : '-'}</span>
                                    ) : (
                                        <InputNumber style={{ width: '100%' }} placeholder="请输入折扣价" precision={2} min={0} />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="topup_plan_desc"
                                    label="方案描述"
                                    className="full-width"
                                    labelCol={{ flex: '90px' }}
                                    wrapperCol={{ flex: 'auto' }}
                                >
                                    {isView ? (
                                        <span className="view-text">{detail.topup_plan_desc || '-'}</span>
                                    ) : (
                                        <TextArea rows={4} placeholder="请输入方案描述（非必填）" />
                                    )}
                                </Form.Item>
                            </div>
                        </Form>
                    </Card>
                </div>

                <div className="footer-actions">
                    {isView ? (
                        <Button type="primary" onClick={onBack}>关闭</Button>
                    ) : (
                        <>
                            <Button onClick={onBack}>取消</Button>
                            <Button style={{ marginLeft: "24px" }} type="primary" onClick={handleSave} loading={submitting} disabled={isEdit && !isFormChanged}>{isEdit ? '保存' : '新增'}</Button>
                        </>
                    )}
                </div>
            </Spin>
        </div>
    );
};

export default PlanEdit;
